import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RepModeProvider, useRepMode } from "./RepModeContext";
import RepIdModal from "./RepIdModal";
import { logout } from "../../../store/actions/userActions";
import wordmark from "../../../assets/brand/cache-wordmark.svg";
import iconMail from "../../../assets/brand/icon-mail.svg";
import { initialsOf } from "../../ForBrands/brandDealUtils";
import { useFitScale, fitScaleStyle } from "../../ForBrands/useFitScale";
import "../../ForBrands/brandMode.css";

// Rep Mode shell (Figma "REP MODE V2"): the shared 72px topbar and tab bar
// with the REP MODE label and the Home / Athletes / My Circle tabs. The
// global Navbar is hidden on /rep routes. An athlete's profile
// (/rep/athletes/:athleteId) keeps the Athletes tab active.

const TABS = [
  { path: "/rep/home", label: "Home", match: ["/rep/home"] },
  { path: "/rep/athletes", label: "Athletes", match: ["/rep/athletes"] },
  { path: "/rep/circle", label: "My Circle", match: ["/rep/circle"] },
];

const displayName = (user) => {
  const first = user?.firstName || (user?.name || "").split(" ")[0] || "";
  const last = user?.lastName || user?.surname || ((user?.name || "").split(" ").slice(1).join(" ") || "");
  const full = `${first} ${last}`.trim();
  return full || user?.email || "Rep";
};

const AvatarMenu = ({ user }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { me, openShare } = useRepMode();
  const name = displayName(user);
  const initials = initialsOf(name.split(" ")[0], name.split(" ").slice(1).join(" "));

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("myEmail");
    localStorage.removeItem("athleteEmail");
    localStorage.removeItem("expiresAt");
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} className="flex items-center gap-4">
        <span className="hidden bm-label-l text-bm-ink sm:inline">{name}</span>
        {user?.avatar ? (
          <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-bm-orange bm-label-xs text-white">{initials}</span>
        )}
      </button>
      {open && (
        <div role="menu" className="bm-fade-in absolute right-0 top-11 z-40 w-60 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl">
          <div className="px-4 pb-2 pt-1">
            <div className="bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted">{user?.email}</div>
            {me.repCode && <div className="mt-1 bm-label-xs text-bm-orange">Rep ID {me.repCode}</div>}
          </div>
          <div className="border-t border-bm-border" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              openShare();
            }}
            className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle"
          >
            Your Rep ID
          </button>
          <Link to="/rep/profile" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Profile
          </Link>
          <Link to="/rep/password" className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Password
          </Link>
          <div className="border-t border-bm-border" />
          <button type="button" onClick={handleLogout} className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

const Shell = () => {
  const location = useLocation();
  const user = useSelector((state) => state.user.user || {});
  const { badges } = useRepMode();
  const fit = useFitScale();
  const activeTab = TABS.find((t) => t.match.some((m) => location.pathname.startsWith(m))) || null;

  return (
    <div className="bm-root bg-white" style={fitScaleStyle(fit)}>
      <header className="bg-white">
        <div className="flex h-[72px] w-full items-center justify-between px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
          <Link to="/rep/home" className="flex items-center" aria-label="Rep Mode home">
            <img src={wordmark} alt="CACHE" className="h-[19px] w-[95px]" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/rep/home#attention" className="flex items-center gap-[6px]" aria-label="Needs your attention">
              <img src={iconMail} alt="" className="h-5 w-5" />
              {badges.attention > 0 && (
                <span className="flex h-[22px] items-center justify-center rounded-full bg-bm-orange px-[9px] bm-label-xs text-white">{badges.attention}</span>
              )}
            </Link>
            <span className="h-5 w-px bg-bm-border" />
            <span className="hidden bm-overline md:inline">Rep Mode</span>
            <AvatarMenu user={user} />
          </div>
        </div>
        <nav className="border-b border-bm-border">
          <div className="flex h-[51px] w-full items-stretch overflow-x-auto px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
            {TABS.map((tab) => {
              const active = tab === activeTab;
              return (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className={`flex shrink-0 items-center gap-2 px-6 font-archivo text-[15px] font-bold leading-5 ${
                    active ? "border-b-2 border-bm-orange text-bm-orange" : "text-bm-muted hover:text-bm-ink"
                  }`}
                >
                  {tab.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </header>
      <main className="w-full px-5 pb-[70px] sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
        <Outlet />
      </main>
      <RepIdModal />
    </div>
  );
};

const RepModeLayout = () => (
  <RepModeProvider>
    <Shell />
  </RepModeProvider>
);

export default RepModeLayout;
